import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Youtube, Filter, List, Grid, Download, BarChart2, TrendingUp, PieChart as PieIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import statisticsService from '../../services/statisticsService';
import { users as userService } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import ManagerFilterPanel from './ManagerFilterPanel';
import { PAGE_KEYS } from '../../constants/savedFilterPageKeys';
import { Pagination } from '@anyemp/ui-kit';
import { PAGINATION_OPTIONS } from '../../utils/paginationConstants';
import Avatar from '../UI/Avatar';
import ManagerYoutubeDetailsModal from './ManagerYoutubeDetailsModal';
import { CHART_COLORS } from '../../utils/chartTheme';
import { ChartCard, TrendLineChart, HorizontalBarChart, DonutChart } from '../../utils/chartComponents';
import MatrixTable from './MatrixTable';
import StatisticsTable from './StatisticsTable';

const ManagerYoutubeStats = () => {
  const getCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(firstDay),
      endDate: formatDate(lastDay)
    };
  };

  const currentMonthDates = getCurrentMonthDates();

  const [stats, setStats] = useState([]);
  const [globalTotals, setGlobalTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20
  });
  const [filters, setFilters] = useState({
    startDate: currentMonthDates.startDate,
    endDate: currentMonthDates.endDate,
    manager_ids: [],
    limit: 20,
    offset: 0,
    period: ''
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [filters.limit, filters.offset]);

  useEffect(() => {
    fetchManagers();
  }, []);

  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && !filters.period) {
      setFilters(prev => ({ ...prev, period: 'day' }));
    }
  }, [viewMode]);

  useEffect(() => {
    if ((viewMode === 'matrix' || viewMode === 'charts') && filters.period) {
      fetchStats();
    }
  }, [filters.period, viewMode]);

  const fetchManagers = async () => {
    try {
      setManagersLoading(true);
      const response = await userService.getAllUsers();

      if (response.success && response.data) {
        const usersList = response.data.users || response.data;
        const managerOptions = Array.isArray(usersList)
          ? usersList.map(user => ({
              value: String(user.id),
              label: user.name || user.email || user.id
            }))
          : [];
        setManagers(managerOptions);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
      toast.error('Failed to load managers list');
    } finally {
      setManagersLoading(false);
    }
  };

  const fetchStats = async (overrideFilters = null) => {
    try {
      setLoading(true);
      setError(null);

      const f = overrideFilters ?? filters;

      const effectivePeriod = viewMode === 'matrix'
        ? (f.period || 'day')
        : f.period;

      const params = {
        ...f,
        manager_ids: f.manager_ids.length > 0 ? f.manager_ids : undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
        period: effectivePeriod || undefined
      };

      const requests = [statisticsService.getManagerYoutubeAdded(params)];

      if (viewMode === 'matrix' || viewMode === 'charts') {
        const totalsParams = {
          startDate: params.startDate,
          endDate: params.endDate,
          manager_ids: params.manager_ids,
          period: params.period
        };
        requests.push(statisticsService.getManagerYoutubeTotals(totalsParams));
      }

      const [response, totalsResponse] = await Promise.all(requests);

      let statsData = [];
      let paginationData = null;

      if (response) {
        if (Array.isArray(response)) {
          statsData = response;
        } else if (response.success && response.data && Array.isArray(response.data)) {
          statsData = response.data;
          paginationData = response.pagination;
        } else if (response.data && Array.isArray(response.data)) {
          statsData = response.data;
          paginationData = response.pagination;
        } else {
          console.warn('Unexpected response format:', response);
        }

        setStats(statsData);

        if (paginationData) {
          const { total, limit, offset } = paginationData;
          setPagination({
            current_page: Math.floor(offset / limit) + 1,
            total_pages: Math.ceil(total / limit),
            total_items: total,
            items_per_page: limit
          });
        }
      }

      if (totalsResponse) {
        const totalsData = totalsResponse.data || totalsResponse;
        setGlobalTotals(totalsData);
      }
    } catch (err) {
      console.error('Error fetching youtube statistics:', err);
      setError(err.message || 'Failed to load statistics');
      toast.error('Failed to load YouTube statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setFilters(prev => ({ ...prev, offset: 0 }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
    fetchStats();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      startDate: currentMonthDates.startDate,
      endDate: currentMonthDates.endDate,
      manager_ids: [],
      limit: 20,
      offset: 0,
      period: (viewMode === 'matrix' || viewMode === 'charts') ? 'day' : ''
    };
    setFilters(resetFilters);
    setPagination({
      current_page: 1,
      total_pages: 1,
      total_items: 0,
      items_per_page: 20
    });
    fetchStats(resetFilters);
  };

  const handlePageChange = (newPage) => {
    const newOffset = (newPage - 1) * filters.limit;
    setFilters(prev => ({ ...prev, offset: newOffset }));
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setFilters(prev => ({ ...prev, limit: newLimit, offset: 0 }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleViewModeChange = (mode) => {
    if (mode === 'matrix' || mode === 'charts') {
      setLoading(true);
    }
    setViewMode(mode);
  };

  const handleRowClick = useCallback((manager) => {
    setSelectedManager(manager);
  }, []);

  const rowKeyManager = useCallback(
    (item) => item.manager?.id || item.manager?.email || JSON.stringify(item),
    []
  );

  const visibleColumns = useMemo(
    () => ({ manager: true, total_videos: true, by_period: !!filters.period }),
    [filters.period]
  );

  const closeModal = () => {
    setSelectedManager(null);
  };

  const exportCSV = () => {
    if (!stats || stats.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Manager', 'Email', 'Total YouTube & Shorts'];
    const rows = [headers];

    stats.forEach(item => {
      const managerInfo = item.manager || {};
      const name = managerInfo.name || 'Unknown';
      const email = managerInfo.email || '';
      rows.push([name, email, item.total_videos || 0]);
    });

    const content = rows
      .map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `youtube-by-managers-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'manager',
        label: 'Manager',
        sortable: true,
        render: (item) => {
          const managerInfo = item.manager || {};
          return (
            <div className="flex min-w-0 items-center gap-3">
              <Avatar
                avatar={managerInfo.avatar}
                name={managerInfo.name || 'Unknown'}
                size="sm"
              />
              <div>
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {managerInfo.name || 'Unknown Manager'}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {managerInfo.email || ''}
                </div>
              </div>
            </div>
          );
        }
      },
      {
        key: 'total_videos',
        label: 'YouTube & Shorts',
        sortable: true,
        render: (item) => (
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {item.total_videos || 0}
          </span>
        )
      }
    ];

    if (filters.period && stats.some(s => s.by_period && s.by_period.length > 0)) {
      cols.push({
        key: 'by_period',
        label: 'By Periods',
        sortable: false,
        render: (item) => {
          if (!item.by_period || item.by_period.length === 0) {
            return <span className="text-gray-400">No data</span>;
          }
          return (
            <div className="flex flex-col gap-1">
              {item.by_period.slice(0, 3).map((period, index) => (
                <div key={index} className="text-xs text-gray-600 dark:text-gray-400">
                  {new Date(period.period).toLocaleDateString()}: {period.count}
                </div>
              ))}
              {item.by_period.length > 3 && (
                <span className="text-xs text-gray-400">
                  +{item.by_period.length - 3} more
                </span>
              )}
            </div>
          );
        }
      });
    }

    return cols;
  }, [filters.period, stats]);

  const generateDateColumns = useMemo(() => {
    if (!filters.startDate || !filters.endDate) return [];

    const period = filters.period || 'day';
    const columns = [];

    const start = new Date(filters.startDate);
    const end = new Date(filters.endDate);

    if (period === 'day') {
      let current = new Date(start);
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];
        const day = current.getDate();
        const month = current.toLocaleDateString('en-US', { month: 'short' });
        const dayOfWeek = current.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        columns.push({
          key: dateStr,
          dateStr,
          label: `${day}`,
          sublabel: isWeekend ? 'wknd' : month,
          isWeekend
        });

        current.setDate(current.getDate() + 1);
      }
    } else if (period === 'week') {
      let current = new Date(start);
      let weekCounter = 1;
      while (current <= end) {
        const dateStr = current.toISOString().split('T')[0];

        columns.push({
          key: dateStr,
          dateStr,
          label: `${weekCounter}`,
          sublabel: 'wk',
          isWeekend: false
        });

        current.setDate(current.getDate() + 7);
        weekCounter++;
      }
    } else if (period === 'month') {
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);

      while (current <= endMonth) {
        const dateStr = current.toISOString().split('T')[0];
        const month = current.toLocaleDateString('en-US', { month: 'short' });
        const year = current.getFullYear();

        columns.push({
          key: dateStr,
          dateStr,
          label: month,
          sublabel: String(year),
          isWeekend: false
        });

        current.setMonth(current.getMonth() + 1);
      }
    }

    return columns;
  }, [filters.startDate, filters.endDate, filters.period]);

  const matrixData = useMemo(() => {
    if (!stats.length || !generateDateColumns.length) return [];

    return stats.map(manager => {
      const periodMap = {};
      (manager.by_period || []).forEach(p => {
        const dateKey = typeof p.period === 'string' ? p.period.split('T')[0] : p.period;
        periodMap[dateKey] = parseInt(p.count) || 0;
      });

      const days = {};
      generateDateColumns.forEach(col => {
        days[col.dateStr] = periodMap[col.dateStr] || 0;
      });

      return { ...manager, days };
    });
  }, [stats, generateDateColumns]);

  const matrixColumnTotals = useMemo(() => {
    if (!globalTotals?.by_period || !generateDateColumns.length) return {};
    const periodMap = {};
    globalTotals.by_period.forEach(p => {
      const dateKey = typeof p.period === 'string' ? p.period.split('T')[0] : p.period;
      periodMap[dateKey] = parseInt(p.count) || 0;
    });
    const totals = {};
    generateDateColumns.forEach(col => {
      totals[col.dateStr] = periodMap[col.dateStr] || 0;
    });
    return totals;
  }, [globalTotals, generateDateColumns]);

  const matrixGrandTotal = globalTotals?.grand_total ?? 0;

  const renderChartsView = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader />
        </div>
      );
    }

    if (!stats || stats.length === 0) {
      return (
        <div className="flex justify-center items-center min-h-[300px] text-gray-400 dark:text-gray-500">
          No data for selected filters
        </div>
      );
    }

    const trendData = (() => {
      if (!globalTotals?.by_period?.length) return [];
      return globalTotals.by_period
        .map(p => ({
          date: typeof p.period === 'string' ? p.period.split('T')[0] : p.period,
          count: parseInt(p.count) || 0
        }))
        .sort((a, b) => a.date.localeCompare(b.date));
    })();

    const managerData = stats
      .map(item => {
        const mgr = item.manager || {};
        const name = mgr.name || `ID: ${item.manager_id?.slice(0, 8)}`;
        return { name, total: item.total_videos || 0 };
      })
      .sort((a, b) => b.total - a.total);

    const pieData = managerData.slice(0, 5).map((item, index) => ({
      name: item.name,
      value: item.total,
      color: CHART_COLORS[index % CHART_COLORS.length]
    }));

    const formatDateLabel = (dateStr) => {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length === 3) return `${parts[2]}.${parts[1]}`;
      return dateStr;
    };

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="YouTube Upload Trend"
          icon={TrendingUp}
          hint={trendData.length === 0 ? 'Select a period grouping (day / week / month) in filters to see the trend' : null}
        >
          <TrendLineChart
            data={trendData}
            color="#ef4444"
            name="YouTube & Shorts"
            tickFormatter={formatDateLabel}
          />
        </ChartCard>

        <ChartCard
          title="YouTube by Manager"
          icon={BarChart2}
          hint={managerData.length === 0 ? 'No data available' : null}
        >
          <HorizontalBarChart
            data={managerData}
            color="#ef4444"
            name="Total YouTube & Shorts"
          />
        </ChartCard>

        <ChartCard
          title="Top Managers Distribution"
          icon={PieIcon}
          hint={pieData.length === 0 ? 'No data available' : null}
        >
          <DonutChart data={pieData} />
        </ChartCard>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <Youtube className="h-8 w-8 text-red-500" />
          <div>
            <h2 className="break-words text-2xl font-bold text-gray-900 dark:text-gray-100">
              YouTube & Shorts by Managers
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Track YouTube and YouTube Shorts added by managers
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex max-w-full overflow-x-auto rounded-lg border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => handleViewModeChange('list')}
              title="List view"
              className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <List className="h-4 w-4" />
              <span>List</span>
            </button>
            <button
              onClick={() => handleViewModeChange('matrix')}
              title="Matrix view by periods"
              className={`flex shrink-0 items-center gap-1.5 border-l px-3 py-2 text-sm transition-colors border-gray-300 dark:border-gray-600 ${
                viewMode === 'matrix'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Grid className="h-4 w-4" />
              <span>Matrix</span>
            </button>
            <button
              onClick={() => handleViewModeChange('charts')}
              title="Charts view"
              className={`flex shrink-0 items-center gap-1.5 border-l px-3 py-2 text-sm transition-colors border-gray-300 dark:border-gray-600 ${
                viewMode === 'charts'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <BarChart2 className="h-4 w-4" />
              <span>Charts</span>
            </button>
          </div>

          {/* Export CSV */}
          <button
            onClick={exportCSV}
            disabled={!stats.length}
            title="Export to CSV"
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" />
            <span>CSV</span>
          </button>

          {/* Filters Toggle */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {filtersExpanded && (
        <ManagerFilterPanel
          filters={filters}
          managers={managers}
          managersLoading={managersLoading}
          viewMode={viewMode}
          onFilterChange={(updates) => setFilters(prev => ({ ...prev, ...updates }))}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
          pageKey={PAGE_KEYS.STATS_MANAGER_YOUTUBE}
        />
      )}

      {/* Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-red-600 dark:text-red-400">
            {error}
          </div>
        ) : viewMode === 'list' ? (
          <StatisticsTable
            data={stats}
            columns={columns}
            onRowClick={handleRowClick}
            isLoading={loading}
            rowKey={rowKeyManager}
            visibleColumns={visibleColumns}
            footer={
              <Pagination
                currentPage={pagination.current_page}
                totalPages={pagination.total_pages}
                totalItems={pagination.total_items}
                itemsPerPage={pagination.items_per_page}
                onPageChange={handlePageChange}
                showResults="range"
                perPageOptions={PAGINATION_OPTIONS}
                perPage={filters.limit}
                onPerPageChange={(v) => handleLimitChange(parseInt(v))}
                showOnSinglePage
                hasFirstLast
                hasGoToPage
              />
            }
          />
        ) : viewMode === 'matrix' ? (
          <MatrixTable
            data={matrixData}
            loading={loading}
            dateColumns={generateDateColumns}
            columnTotals={matrixColumnTotals}
            grandTotal={matrixGrandTotal}
            totalKey="total_videos"
            startIndex={filters.offset}
            onManagerClick={handleRowClick}
            pagination={pagination}
            onPageChange={handlePageChange}
            itemsPerPage={filters.limit}
          />
        ) : (
          renderChartsView()
        )}
      </div>

      {/* Details Modal */}
      {selectedManager && (
        <ManagerYoutubeDetailsModal
          manager={selectedManager}
          filters={filters}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default ManagerYoutubeStats;
